/**
 * Supplier Form Page - Create/Edit Supplier
 * Leviathan - Fuel Jobber Back Office
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Building2, MapPin, FileText, CreditCard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface SupplierFormData {
  supplierNumber: string;
  name: string;
  displayName: string;
  /**
   * NOTE: this form used `contactName`, which the Supplier model does not
   * have — it has primaryContact. So this field never saved and never loaded;
   * the page looked like it worked. Found while splitting contact names.
   */
  primaryContactFirstName: string;
  primaryContactLastName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  taxId: string;
  accountNumber: string;
  paymentTerms: string;
  creditLimit: number;
  notes: string;
  isActive: boolean;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const PAYMENT_TERMS = [
  { value: 'NET_10', label: 'Net 10' },
  { value: 'NET_15', label: 'Net 15' },
  { value: 'NET_30', label: 'Net 30' },
  { value: 'NET_45', label: 'Net 45' },
  { value: 'NET_60', label: 'Net 60' },
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
  { value: 'PREPAID', label: 'Prepaid' },
];

export default function SupplierFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<SupplierFormData>({
    supplierNumber: '',
    name: '',
    displayName: '',
    primaryContactFirstName: '',
    primaryContactLastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    taxId: '',
    accountNumber: '',
    paymentTerms: 'NET_30',
    creditLimit: 0,
    notes: '',
    isActive: true,
  });

  useEffect(() => {
    if (isEditMode && id) {
      fetchSupplier(id);
    }
  }, [id, isEditMode]);

  const fetchSupplier = async (supplierId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/suppliers/${supplierId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch supplier');
      const data = await response.json();
      setFormData({
        supplierNumber: data.supplierNumber || '',
        name: data.name || '',
        displayName: data.displayName || '',
        primaryContactFirstName: data.primaryContactFirstName || '',
        primaryContactLastName: data.primaryContactLastName || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        taxId: data.taxId || '',
        accountNumber: data.accountNumber || '',
        paymentTerms: data.paymentTerms || 'NET_30',
        creditLimit: data.creditLimit || 0,
        notes: data.notes || '',
        isActive: data.isActive ?? true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Supplier name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isEditMode ? `${API_URL}/api/suppliers/${id}` : `${API_URL}/api/suppliers`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save supplier');
      }

      navigate('/suppliers');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-secondary">Loading supplier...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/suppliers')}
            className="p-2 hover:bg-panel-dark rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              {isEditMode ? 'Edit Supplier' : 'New Supplier'}
            </h1>
            <p className="text-text-secondary text-sm">
              {isEditMode ? 'Update supplier information' : 'Add a new fuel supplier'}
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Supplier'}
        </button>
      </div>

      {error && (
        <div className="bg-error-red/10 border border-error-red/30 text-error-red px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Supplier Information */}
        <div className="bg-panel-dark border border-panel-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-amber" />
            <h2 className="text-lg font-semibold text-text-primary">Supplier Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Supplier Number
              </label>
              <input
                type="text"
                name="supplierNumber"
                value={formData.supplierNumber}
                onChange={handleChange}
                placeholder="Auto-generated if empty"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Company Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Display Name
              </label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Short name for reports"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Contact First Name
              </label>
              <input
                type="text"
                name="primaryContactFirstName"
                value={formData.primaryContactFirstName}
                onChange={handleChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Contact Last Name
              </label>
              <input
                type="text"
                name="primaryContactLastName"
                value={formData.primaryContactLastName}
                onChange={handleChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-field w-full"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="w-4 h-4 rounded border-border-dark bg-panel-dark text-accent-teal focus:ring-accent-teal"
              />
              <label htmlFor="isActive" className="text-sm text-text-secondary">
                Active Supplier
              </label>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-panel-dark border border-panel-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-amber" />
            <h2 className="text-lg font-semibold text-text-primary">Address</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Street Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="input-field w-full"
              >
                <option value="">Select State</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                ZIP Code
              </label>
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
                className="input-field w-full"
              />
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="bg-panel-dark border border-panel-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-amber" />
            <h2 className="text-lg font-semibold text-text-primary">Business Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Tax ID</label>
              <input
                type="text"
                name="taxId"
                value={formData.taxId}
                onChange={handleChange}
                placeholder="XX-XXXXXXX"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Account Number
              </label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                placeholder="Your account with supplier"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Payment Terms
              </label>
              <select
                name="paymentTerms"
                value={formData.paymentTerms}
                onChange={handleChange}
                className="input-field w-full"
              >
                {PAYMENT_TERMS.map((term) => (
                  <option key={term.value} value={term.value}>
                    {term.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Credit Limit ($)
              </label>
              <input
                type="number"
                name="creditLimit"
                value={formData.creditLimit}
                onChange={handleChange}
                min="0"
                step="1000"
                className="input-field w-full"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-panel-dark border border-panel-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-amber" />
            <h2 className="text-lg font-semibold text-text-primary">Notes</h2>
          </div>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Internal notes about this supplier..."
            className="input-field w-full"
          />
        </div>
      </form>
    </div>
  );
}
